import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { AxiosError } from 'axios'
import { SSE } from 'sse.js'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import apis from '../../apis'
import { getJwtToken, useJwtToken } from '../../auth'
import { axiosGet, axiosPost, axiosPut } from '../../axiosHelper'
import { CustomRouter } from '../../hooks/common/useAppRouter'
import {
  conversationCreditBundleSummary,
  conversationCreditBundleThreads,
  conversationCreditsSummary,
  conversationSetting,
  conversationThread,
  conversationThreads,
  conversationThreadsSummary,
  conversationThreadsTurnCnt,
} from '../../react-query/queryApis'
import urls from '../../urls'
import { CONVERSATION_ROLE, COURSE_PROMPT, INSTRUCTION_PROMPT, ROLEPLAY_PROMPT } from '../../vars'
import { createAxiosTokenConfig } from '../axios-util'

export interface IAiTutorResponse {
  thread_id: string
  assistant_id: string
  text: string
  audio: string
}

export const getThreads = async (selectedDate, status = null) => {
  try {
    let response
    if (status) {
      response = await axiosGet(apis.conversation.threads.status({ date: selectedDate, ...status }))
    } else {
      response = await axiosGet(apis.conversation.threads.index({ date: selectedDate }))
    }
    return response
  } catch (e) {
    alert(e)
  }
}

export const getThreadMessages = async (threadId) => {
  try {
    const response = await axiosGet(apis.conversation.threads.messages({ thread_id: threadId }))
    return response
  } catch (e) {
    alert(e)
  }
}

export const getInstruction = async (courseId, prefer_course_question = false) => {
  console.log(
    '🔵🔵🔵 url ->',
    apis.conversation.threads.instruction({ courseId: courseId, prefer_course_question: prefer_course_question })
  )
  try {
    const response = await axiosGet(
      apis.conversation.threads.instruction({ courseId: courseId, prefer_course_question: prefer_course_question })
    )
    return response
  } catch (e) {
    alert(e)
  }
}

export const getThreadCourseQuestions = async (threadId: number) => {
  try {
    const response = await axiosGet(apis.conversation.questions.get(threadId))
    return response
  } catch (e) {
    alert(e)
  }
}

export const getInstructionRoleplay = async (roleplayId) => {
  try {
    const response = await axiosGet(apis.aiTutor.roleplay.detail({ roleplayId: roleplayId }))
    return response?.roleplay?.prompt
  } catch (e) {
    alert(e)
  }
}

export const updateThreadGoal = async (thread_id, goal) => {
  try {
    const response = await axiosPost(apis.conversation.threads.updateGoal(thread_id), {
      goal: goal,
    })
    return response
  } catch (e) {
    alert(e)
  }
}

export const updateThreadQuestionCount = async (threadId, questionCount) => {
  try {
    const response = await axiosPut(apis.conversation.questions.count({ threadId: threadId, count: questionCount }), {})
    return response
  } catch (e) {
    alert(e)
  }
}

export const createThread = async (
  instruction,
  material: { courseId?: number; roleplayId?: number },
  prefer_course_question = false
) => {
  try {
    const response = await axiosPost(apis.conversation.threads.index(), {
      instruction: instruction,
      course_id: material.courseId,
      ai_tutor_roleplay_id: material.roleplayId,
      prefer_course_question: prefer_course_question,
    })
    return response
  } catch (e) {
    alert(e)
  }
}

export const createPromptMessage = async (threadId, text, role) => {
  try {
    const response = await axiosPost(apis.conversation.threads.message(threadId), {
      text: text,
      role: role,
    })
    return response
  } catch (e) {
    alert(e)
  }
}

export const getThreadDetail = async (threadId) => {
  try {
    const response = await axiosGet(apis.conversation.threads.detail(threadId))
    return response
  } catch (e) {
    alert(e)
  }
}

export const getThreadSummary = async (threadId) => {
  try {
    const response = await axiosGet(apis.conversation.threads.messageCount(threadId))
    return response
  } catch (e) {
    alert(e)
  }
}

export const getCourses = async () => {
  try {
    const response = await axiosGet(apis.conversation.threads.courses())
    return response
  } catch (e) {
    alert(e)
  }
}

export const endThread = async (threadId) => {
  try {
    const response = await axiosPost(apis.conversation.threads.end(threadId))
    return response
  } catch (e) {
    alert(e)
  }
}

export const sendFeedback = async (threadId, data) => {
  try {
    const response = await axiosPost(apis.conversation.threads.feedback(threadId), {
      data: data,
    })
    return response
  } catch (e) {
    alert(e)
  }
}

export const requestThreadAnalysis = async (threadId) => {
  const data = await axiosPost(apis.conversation.threads.analysis(threadId))
  return data
}

// GET v5/student/conversation/credits/summary
// AI 스피킹 파트너 이용권 보유 현황 요약 정보 조회 API
interface IConversationCreditsSummary {
  total_credit_count: number
  usable_credit_count: number
  unusable_credit_count: number
}

export const useConversationCreditsSummary = () => {
  const token = useJwtToken()
  return useQuery<IConversationCreditsSummary, AxiosError>({
    queryKey: conversationCreditsSummary.getQueryKey(),
    queryFn: conversationCreditsSummary.getQueryFn(),
    retry: false,
    refetchOnWindowFocus: true,
    enabled: !!token,
  })
}

// GET v5/student/conversation/threads/summary
// 완료, 진행중인 대화 개수 요약 정보 조회 API
interface IConversationThreadsSummary {
  in_progress_thread_count: number
  not_evaluated_thread_count: number
  evaluated_therad_count: number
  expired_thread_count: number
  completed_thread_count: number
  total_thread_count: number
  thread_count_this_week: number
  remaining_credit_count: number
  has_analysis_permission: boolean
}

export const useConversationThreadsSummary = () => {
  const token = getJwtToken()
  return useQuery<IConversationThreadsSummary, AxiosError>({
    queryKey: conversationThreadsSummary.getQueryKey(),
    queryFn: conversationThreadsSummary.getQueryFn(),
    retry: false,
    refetchOnWindowFocus: true,
    enabled: !!token,
  })
}

// GET v5/student/conversation/threads/:thread_id/summary
// 완료, 진행중인 대화 개수 요약 정보 조회 API
interface IConversationThreadTurnCnt {
  user_message_count: number
}

export const useConversationThreadsTurnCnt = (threadId) => {
  const pathParams = { threadId }

  return useQuery<IConversationThreadTurnCnt, AxiosError>({
    queryKey: conversationThreadsTurnCnt.getQueryKey({ pathParams }),
    queryFn: conversationThreadsTurnCnt.getQueryFn({ pathParams }),
  })
}

// GET v5/student/conversation/credit_bundle/:credit_bundle_id/summary
// AI 스피킹 이용권 Bundle 상세 조회 API
export interface IConversationCreditBundleSummary {
  credit_bundle: CreditBundle
}

export interface CreditBundle {
  id: number
  type: string
  formatted_created_at: string
  credit_counts: CreditCounts
  expiration_info: ExpirationInfo
}

export interface CreditCounts {
  expired_or_refunded_credit_count: number
  usable_credit_count: number
  unusable_credit_count: number
  total_credit_count: number
}

export interface ExpirationInfo {
  formatted_expired_at: string
  days_until_expired_at: number
}

export const useConversationCreditBundleSummary = ({ bundleId }: { bundleId: number }) => {
  return useQuery<IConversationCreditBundleSummary, AxiosError>({
    queryKey: conversationCreditBundleSummary.getQueryKey({ pathParams: { bundleId } }),
    queryFn: conversationCreditBundleSummary.getQueryFn({ pathParams: { bundleId } }),
    retry: false,
    refetchOnWindowFocus: true,
  })
}

// GET v5/student/conversation/credit_bundle/:credit_bundle_id/threads
// AI 스피킹 이용권 Bundle 관련 대화 목록 조회 API
export interface IConversationHistory {
  id: number
  title: string
  sub_title: string
  status: string
  created_at: Date
  formatted_created_at: string
}

export const useConversationCreditBundleThreads = ({ bundleId }: { bundleId: number }) => {
  return useQuery<IConversationHistory[], AxiosError>({
    queryKey: conversationCreditBundleThreads.getQueryKey({ pathParams: { bundleId } }),
    queryFn: conversationCreditBundleThreads.getQueryFn({ pathParams: { bundleId } }),
    retry: false,
    refetchOnWindowFocus: true,
  })
}

// Filter
export enum CONVERSATION_FILTER_TYPE {
  ENTIRE = 'entire',
  ANALYSIS = 'analysis',
  ROLEPLAY = 'roleplay',
  DEBATE = 'debate',
  CALL = 'debate_voip',
}

// GET v5/student/conversation/threads
// Threads 조회 API
interface IConversationThreads {
  assistant_type: CONVERSATION_FILTER_TYPE
  created_at: string
  formatted_created_at: string
  id: number
  status: string | 'in_progress'
  sub_title: string
  title: string
}

export const useConversationThreads = ({ date }: { date?: Date } = {}) => {
  return useQuery<IConversationThreads[], AxiosError>({
    queryKey: conversationThreads.getQueryKey({ pathParams: { date } }),
    queryFn: conversationThreads.getQueryFn({ pathParams: { date } }),
    retry: false,
    refetchOnWindowFocus: true,
  })
}

export const useConversationUserPreferenceEdit = () => {
  const token = getJwtToken()
  return useMutation({
    mutationFn: async (body: { key: string; value: boolean }) => {
      await axios.put(apis.course.edit_tooltip(body.key, body.value), null, createAxiosTokenConfig(token))
    },
    // onError: (error) => {
    //   console.log('error', error)
    //   toast(t(s_caf.highlight_list_edit.toast3))
    // },
  })
}

export const createConversationAi = async (
  material: { courseId?: number; roleplayId?: number; isLivekit?: boolean },
  router: CustomRouter,
  getFullUrl: (string) => string,
  goal?: string,
  prefer_course_question?: boolean
) => {
  let instruction

  if (material.courseId) instruction = (await getInstruction(material.courseId))?.course_instruction
  else if (material.roleplayId) {
    //here when doing roleplaying.
    const r = await getInstructionRoleplay(material.roleplayId)
    instruction = ROLEPLAY_PROMPT(r.guide, r.persona, r.summary, r.situation)
  }

  //이게 원래 오래 걸리는 그런건가?
  const response = await createThread(instruction, material, prefer_course_question)

  if (!response) {
    router.push(urls.webview.conversationalAI.list, {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: { url: getFullUrl(urls.webview.conversationalAI.list) },
    })
  }

  const threadId = response.thread_id
  const roomToken = response.room_token //if exist.

  if (material?.courseId) {
    const [[courseInfo, instructionPrompt], _] = await Promise.all([
      (async () => {
        const courseInfo = createPromptMessage(threadId, COURSE_PROMPT(instruction), CONVERSATION_ROLE.SYSTEM)
        const instructionPrompt = createPromptMessage(threadId, INSTRUCTION_PROMPT, CONVERSATION_ROLE.SYSTEM)
        return [courseInfo, instructionPrompt]
      })(),
      updateThreadGoal(threadId, goal),
    ])

    if (prefer_course_question) {
      router.push(urls.webview.aiTutor.discussion.questionSelection(material?.courseId, threadId), {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.aiTutor.discussion.questionSelection(material?.courseId, threadId)),
        },
      })
    } else {
      router.push(urls.webview.conversationalAI.main(threadId), {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.conversationalAI.main(threadId)),
        },
      })
    }
    return { courseInfo, instructionPrompt }
  } else {
    console.log('여기가 실행되겠군')
    const courseInfo = await createPromptMessage(threadId, instruction, CONVERSATION_ROLE.SYSTEM)

    if (material.isLivekit) {
      router.push(urls.webview.aiTutor.roleplayLivekit(), {
        appScreenName: APP_SCREEN_NAME.ROLEPLAY_LIVEKIT,
        appSendData: {
          url: getFullUrl(urls.webview.aiTutor.roleplayLivekit()),
          threadId: threadId,
          roomToken: roomToken,
          userName: 'username default',
        },
      })
    } else {
      router.push(urls.webview.aiTutor.roleplay(threadId), {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.aiTutor.roleplay(threadId)),
        },
      })
    }

    return { courseInfo }
  }
}

export const enterConversationAi = async (
  material: { courseId?: number; roleplayId?: number },
  router: CustomRouter,
  getFullUrl: (string) => string,
  conversationWebviewOn: any = false,
  ignoreInstruction: boolean = false
) => {
  // const ignoreWelcome = await axiosGet(apis.conversation.users.getSetting('conversation_ignore_instruction'))
  // 롤플레이는 무조건
  if (material?.roleplayId) {
    router.push(urls.webview.aiTutor.roleplayInstruction(material), {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: { url: getFullUrl(urls.webview.aiTutor.roleplayInstruction(material)) },
    })
  } else {
    router.push(urls.webview.aiTutor.discussion.instruction(material.courseId), {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: { url: getFullUrl(urls.webview.aiTutor.discussion.instruction(material.courseId)) },
    })
  }
}

export const useConversationThread = (threadId: number) => {
  return useQuery<any, AxiosError>({
    queryKey: conversationThread.getQueryKey({ pathParams: threadId }),
    queryFn: conversationThread.getQueryFn({ pathParams: threadId }),
  })
}

interface IConversationSetting {
  speed: number
  difficulty: number
  minimum_word?: number | null
  pronunciation: string
  prefer_course_question: boolean
}

export const useConversationSetting = () => {
  return useQuery<IConversationSetting, AxiosError>({
    queryKey: conversationSetting.getQueryKey(),
    queryFn: conversationSetting.getQueryFn(),
  })
}

export const useConversationSettingSetSpeed = () => {
  const token = getJwtToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (value: number) => {
      await axios.put(apis.conversation.setting.speed(value), null, createAxiosTokenConfig(token))
    },
    onSuccess: () => {
      queryClient.invalidateQueries(conversationSetting.getQueryKey())
    },
  })
}

export const useConversationSettingSetPronunciation = () => {
  const token = getJwtToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (value: string) => {
      await axios.put(apis.conversation.setting.pronunciation(value), null, createAxiosTokenConfig(token))
    },
    onSuccess: () => {
      queryClient.invalidateQueries(conversationSetting.getQueryKey())
    },
  })
}

export const useConversationSettingSetDifficulty = () => {
  const token = getJwtToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (value: number) => {
      await axios.put(apis.conversation.setting.difficulty(value), null, createAxiosTokenConfig(token))
    },
    onSuccess: () => {
      queryClient.invalidateQueries(conversationSetting.getQueryKey())
    },
  })
}

export const useConversationSettingSetPreferCourseQuestion = () => {
  const token = getJwtToken()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (value: boolean) => {
      await axios.put(apis.conversation.setting.preferCourseQuestion(value), null, createAxiosTokenConfig(token))
    },
    onSuccess: () => {
      queryClient.invalidateQueries(conversationSetting.getQueryKey())
    },
  })
}

export const uploadAudioToS3 = async (audioBufferArray: ArrayBuffer, threadId: string, role, index) => {
  const url = apis.conversation.threads.audio(threadId)
  let attempts = 0
  const maxAttempts = 3

  while (attempts < maxAttempts) {
    try {
      // upload as File via axios.post
      const file = new File([audioBufferArray], 'audio.wav', { type: 'audio/wav' })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('role', role)
      formData.append('idx', index)
      const res = await axiosPost(url, formData)
      if (!res.path) throw new Error('Failed to upload audio')
      return res.path
    } catch (error) {
      attempts += 1
      console.error('Error fetching AI response:', error)
      // if (attempts >= maxAttempts) {
      //   navigationRef?.current?.navigate(NAV_NAMES.ConversationErrorDialog)
      //   break
      // }
    }
  }
}

export const getResponseFromAI = async (messages) => {
  console.log('😎', messages)
  const url = apis.text
  let attempts = 0
  const maxAttempts = 3

  while (attempts < maxAttempts) {
    try {
      const res = await axiosPost(url, {
        messages: messages,
      })
      console.log(JSON.parse(res))
      return JSON.parse(res)
    } catch (error) {
      attempts += 1
      console.error('Error fetching getResponseFromAI response:', error)
    }
  }
  console.log('😎😎')
}

export const getStreamResonseFromAI = async (messages, updateHandler: any = null) => {
  const url = apis.textStream
  let attempts = 0
  const maxAttempts = 3

  console.log('▶️▶️▶️▶️ get stream response from ai', messages)

  while (attempts < maxAttempts) {
    try {
      const sse = new (SSE as any)(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        payload: JSON.stringify({ messages }),
      })
      let buf = ''
      sse.addEventListener('message', (e) => {
        // console.log('e.data ====>', e.data)
        const { delta } = JSON.parse(e.data)

        //이 버퍼는 데이터. 이 buf는 모든 대사가 제생이 되고 난 뒤에, 보여주기를 위한 데이터.
        buf += JSON.parse(e.data).delta

        if (updateHandler) updateHandler(delta)
      })
      return new Promise<string>((resolve) => {
        const intv = setInterval(() => {
          if (sse.readyState === 2) {
            clearInterval(intv)
            resolve(buf)
          }
        }, 10)
      })
    } catch (error) {
      attempts += 1
      console.error('Error fetching getStreamResonseFromAI response:', error)
      // TODO DAHO popup handle
      // if (attempts >= maxAttempts) {
      //   navigationRef?.current?.navigate(NAV_NAMES.ConversationErrorDialog)
      //   break
      // }
    }
  }
}

//get
export const getTextResponseFromAI = async (
  messages: Array<{ role: string; content: string }>,
  updateHandler: any = null
): Promise<string> => {
  const url = apis.textChunk // (혹은 바뀐 REST 엔드포인트)
  let attempts = 0
  const maxAttempts = 3

  console.log('▶️▶️▶️ get full response from ai', messages)

  while (attempts < maxAttempts) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      // 1) JSON 파싱
      const { text } = await res.json()
      // 2) (선택) updateHandler로 점진적 렌더링이 필요하면
      if (updateHandler) updateHandler(text)

      return text
    } catch (error) {
      attempts += 1
      console.error('Error fetching full AI response:', error)
      if (attempts >= maxAttempts) throw error
      await new Promise((r) => setTimeout(r, 300))
    }
  }
  // 여길 절대 안 탈 거예요.
  return ''
}

export const getAudioBySentence = async (sentence, voice, instruction) => {
  const url = apis.ttsStream
  let attempts = 0
  const maxAttempts = 3

  while (attempts < maxAttempts) {
    try {
      const res = await fetch(url, {
        body: JSON.stringify({ input: sentence, voice: voice, instructions: instruction }),
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (!res.ok) throw new Error('Failed to fetch audio')
      return res.body?.getReader()
    } catch (error) {
      attempts += 1
      console.error('Error fetching AI response:', error)
      await new Promise((r) => setTimeout(r, 300))
      // TODO DAHO popup handle
      // if (attempts >= maxAttempts) {
      //   navigationRef?.current?.navigate(NAV_NAMES.ConversationErrorDialog)
      //   break
      // }
    }
  }
}

export const getChunkAudioBySentence = async (sentence, voice, instruction) => {
  const url = apis.ttsChunk
  let attempts = 0
  const maxAttempts = 3

  while (attempts < maxAttempts) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input: sentence, voice, instructions: instruction }),
      })
      if (!res.ok) throw new Error(`TTS HTTP ${res.status}`)

      // 한 번에 ArrayBuffer로
      return await res.arrayBuffer()
    } catch (error) {
      attempts += 1
      console.error('Error fetching AI response:', error)
      await new Promise((r) => setTimeout(r, 300))
      // TODO DAHO popup handle
      // if (attempts >= maxAttempts) {
      //   navigationRef?.current?.navigate(NAV_NAMES.ConversationErrorDialog)
      //   break
      // }
    }
  }
}

type UserSettingResponseType = {
  value?: any
}

/* tool tip 사용 여부 판단 API (get, put) */
export const useConversationGetUserSetting = (key: string) => {
  const token = getJwtToken()

  return useQuery<UserSettingResponseType, AxiosError>({
    queryKey: [apis.conversation.users.getSetting(key)],
    queryFn: async () => {
      const { data } = await axios.get(apis.conversation.users.getSetting(key), createAxiosTokenConfig(token))
      console.log('QUERY RESPONSE :: ', data)
      return data
    },
    retry: false,
    // onError: (err) => {
    //   console.log({ err })
    // },
  })
}

export const useConversationEditUserSetting = () => {
  const token = getJwtToken()

  return useMutation({
    mutationFn: async (body: { key: string; value: boolean }) => {
      await axios.put(apis.conversation.users.updateSetting(body.key, body.value), null, createAxiosTokenConfig(token))
    },
    // onError: (error) => {
    //   console.log('error', error)
    //   toast(t(s_caf.highlight_list_edit.toast3))
    // },
  })
}

export type ConversationQuestionResponseType = {
  content_main: string
  content_main_ko: string
  question_id: number
  question_number: string
  conversation_selection_count?: number
  is_selected_questions_from_lesson?: boolean
}

export const useConversationCourseQuestions = ({ courseId }: { courseId: number }) => {
  const token = getJwtToken()

  return useQuery<ConversationQuestionResponseType[], AxiosError>({
    queryKey: [apis.conversation.questions.index({ courseId: courseId })],
    queryFn: async () => {
      const { data } = await axios.get(
        apis.conversation.questions.index({ courseId: courseId }),
        createAxiosTokenConfig(token)
      )
      console.log('QUERY RESPONSE :: ', data)
      return data
    },
    retry: false,
  })
}
